<script setup lang="ts">
import AppContextPanel from '@/components/app-shell/AppContextPanel.vue'
import CreditAssetsTable from '@/components/credit/CreditAssetsTable.vue'
import CreditHouseholdOverview from '@/components/credit/CreditHouseholdOverview.vue'
import CreditIncomeTable from '@/components/credit/CreditIncomeTable.vue'
import CreditPositionsTable from '@/components/credit/CreditPositionsTable.vue'
import DocumentsSection from '@/components/documents/DocumentsSection.vue'
import FinestAssistantPanel from '@/components/FinestAssistantPanel.vue'
import FinestListItem from '@/components/FinestListItem.vue'
import FinestMasterDetailCard from '@/components/FinestMasterDetailCard.vue'
import FinestMasterListGroup from '@/components/FinestMasterListGroup.vue'
import SectionTabs, { SectionTab } from '@/components/SectionTabs.vue'
import { usePdfExport } from '@/composables/usePdfExport'
import { missingPerCreditTab } from '@/pages/investors/credit/tabs'
import { useFeatureFlagStore } from '@/stores/features'
import type { PageProps } from '@/types'
import { initials } from '@/utils/initials'
import { Head, usePage } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { assetGroupKeys } from './categories'
import { seedCreditState } from './creditPositions'
import { householdOverview } from './householdOverview'
import { incomeGroupKeys } from './incomeSuggestions'
import type { CreditPerson, CreditTab, PositionCategory } from './types'

const props = defineProps<{
  people: CreditPerson[]
  incomeYears: number[]
  creditCompletion: number
  documentTypeOptions: App.Data.Documents.DocumentTypeOptionData[]
}>()

const { t } = useI18n()

const page = usePage<PageProps>()

const activeTab = ref<CreditTab>('income')

const HOUSEHOLD_VIEW = 'household'

const selectedPersonId = ref<string>(HOUSEHOLD_VIEW)

const selectedPerson = computed<CreditPerson | null>(
  () => props.people.find((person) => person.id === selectedPersonId.value) ?? null,
)

const householdSelected = computed(() => selectedPersonId.value === HOUSEHOLD_VIEW)

const tabs = computed<SectionTab<CreditTab>[]>(() => {
  const missing = missingPerCreditTab(selectedPerson.value?.fieldStates ?? {})

  return [
    ...(['income', 'expenses', 'assets', 'liabilities'] as const).map((tab) => ({
      label: t(`tabs.${tab}`),
      value: tab,
      count: missing[tab],
    })),
    { label: t('tabs.documents'), value: 'documents' as const, icon: 'pi pi-file', trailing: true },
  ]
})

const positionCategory = computed<PositionCategory | null>(() =>
  activeTab.value === 'expenses' || activeTab.value === 'liabilities' ? activeTab.value : null,
)

const displayName = (person: CreditPerson): string => person.name.trim() || t('unnamedPerson')

const missingCount = (person: CreditPerson): number =>
  Object.values(person.fieldStates).filter((field) => field.state === 'missing').length

const householdMissingCount = computed(() => props.people.reduce((sum, person) => sum + missingCount(person), 0))

const serverRows = computed(() =>
  seedCreditState(props.people, props.incomeYears, {
    placeholder: t('announced.placeholder'),
    hint: (source: string) => t('announced.hint', { source }),
  }),
)

const forecastYear = computed(() => props.incomeYears[props.incomeYears.length - 1])

const overview = computed(() =>
  householdOverview({
    people: props.people,
    incomeRows: serverRows.value.incomeRows,
    assetRows: serverRows.value.assetRows,
    expenses: serverRows.value.positions.expenses,
    liabilities: serverRows.value.positions.liabilities,
    year: forecastYear.value,
  }),
)

const features = useFeatureFlagStore()

const { generatePdf, previewPdf, generatingPdf } = usePdfExport()

const exportPdf = (): Promise<void> =>
  generatePdf({ template: 'self-disclosure', id: page.props.auth.user.id ?? undefined })

const exportActions = computed(() =>
  features.enabled('previewPdf')
    ? [
        {
          label: t('export.preview'),
          icon: 'pi pi-expand',
          command: () => previewPdf({ template: 'self-disclosure' }),
        },
      ]
    : [],
)
</script>

<template>
  <Head :title="t('heading')" />

  <AppContextPanel>
    <FinestAssistantPanel :completion="creditCompletion" context="credit" />
  </AppContextPanel>

  <div class="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
    <div>
      <p class="section-label">{{ t('eyebrow') }}</p>
      <div class="flex items-center gap-2">
        <h1 class="page-title">{{ t('heading') }}</h1>
        <PageTitleHelp :text="t('subtitle')" />
      </div>
    </div>

    <div class="flex-none">
      <SplitButton
        v-if="exportActions.length"
        :label="t('export.action')"
        icon="pi pi-file-pdf"
        :model="exportActions"
        :loading="generatingPdf"
        @click="exportPdf" />
      <Button v-else :label="t('export.action')" icon="pi pi-file-pdf" :loading="generatingPdf" @click="exportPdf" />
    </div>
  </div>

  <FinestMasterDetailCard class="flex-1">
    <template #master>
      <div class="flex flex-col gap-5">
        <FinestListItem
          :label="t('overview')"
          icon="pi pi-chart-bar"
          :selected="householdSelected"
          :count="householdMissingCount"
          :count-label="t('missingData', { count: householdMissingCount })"
          @click="selectedPersonId = HOUSEHOLD_VIEW" />

        <FinestMasterListGroup :label="t('household')" icon="pi pi-home">
          <FinestListItem
            v-for="person in people"
            :key="person.id"
            :label="displayName(person)"
            :sublabel="t(`roles.${person.role}`)"
            :selected="person.id === selectedPersonId"
            :count="missingCount(person)"
            :count-label="t('missingData', { count: missingCount(person) })"
            @click="selectedPersonId = person.id" />
        </FinestMasterListGroup>
      </div>
    </template>

    <template #detail>
      <CreditHouseholdOverview
        v-if="householdSelected"
        :income="overview.income"
        :expenses="overview.expenses"
        :assets="overview.assets"
        :liabilities="overview.liabilities"
        :debt-ratio="overview.debtRatio" />

      <div v-else-if="selectedPerson">
        <div class="mb-5 flex items-center gap-3">
          <span
            class="bg-surface-3 text-ink-2 flex h-10 w-10 flex-none items-center justify-center rounded-full text-sm font-bold">
            {{ initials(displayName(selectedPerson)) }}
          </span>
          <h2 class="text-xl font-bold">{{ displayName(selectedPerson) }}</h2>
        </div>

        <SectionTabs v-model="activeTab" :tabs="tabs" :aria-label="t('heading')" />

        <div v-if="activeTab === 'income'" class="flex flex-col gap-6">
          <CreditIncomeTable
            v-for="group in incomeGroupKeys"
            :key="`${selectedPerson.id}-${group}`"
            :person-id="selectedPerson.id"
            :server-rows="serverRows.incomeRows[selectedPerson.id][group]"
            :group="group"
            :years="incomeYears" />
        </div>

        <div v-else-if="activeTab === 'assets'" class="flex flex-col gap-6">
          <CreditAssetsTable
            v-for="group in assetGroupKeys"
            :key="`${selectedPerson.id}-${group}`"
            :person-id="selectedPerson.id"
            :server-rows="serverRows.assetRows[selectedPerson.id][group]"
            :group="group" />
        </div>

        <CreditPositionsTable
          v-else-if="positionCategory"
          :key="`${selectedPerson.id}-${positionCategory}`"
          :person-id="selectedPerson.id"
          :server-rows="serverRows.positions[positionCategory][selectedPerson.id]"
          :category="positionCategory" />

        <DocumentsSection
          v-else-if="activeTab === 'documents'"
          :section="selectedPerson.documents"
          :type-options="documentTypeOptions"
          :context-label="displayName(selectedPerson)" />
      </div>

      <p v-else class="text-ink-3 py-12 text-center text-sm">{{ t('emptyPeople') }}</p>
    </template>
  </FinestMasterDetailCard>
</template>

<i18n>
{
  "de": {
    "eyebrow": "Meine Basis",
    "heading": "Bonität",
    "subtitle": "Einkommen, Ausgaben, Vermögen und Verbindlichkeiten – einmal pflegen, bei jeder Finanzierungsanfrage automatisch aktuell mitgeschickt.",
    "emptyPeople": "Noch keine Person erfasst.",
    "unnamedPerson": "Person ohne Namen",
    "missingData": "{count} fehlende Angaben",
    "announced": {
      "placeholder": "wird ausgelesen …",
      "hint": "Wird aus deinem Beleg „{source}“ ausgelesen. Du kannst den Wert jederzeit selbst eintragen."
    },
    "household": "Ich & Familie",
    "overview": "Gesamtübersicht",
    "export": {
      "action": "Als PDF exportieren",
      "preview": "Vorschau",
      "summary": "PDF wird generiert",
      "detail": "Der Download startet automatisch, sobald der Export generiert wurde."
    },
    "roles": {
      "primary": "Ich",
      "partner": "Partner",
      "child": "Kind",
      "other": "Weitere Person"
    },
    "tabs": {
      "income": "Einkommen",
      "expenses": "Ausgaben",
      "assets": "Vermögen",
      "liabilities": "Verbindlichkeiten",
      "documents": "Dokumente"
    }
  },
  "en": {
    "eyebrow": "My Base",
    "heading": "Credit",
    "subtitle": "Income, expenses, assets and liabilities – maintain once, automatically kept up to date with every financing request.",
    "emptyPeople": "No person recorded yet.",
    "unnamedPerson": "Unnamed person",
    "missingData": "{count} missing details",
    "announced": {
      "placeholder": "being read …",
      "hint": "Being read from your \"{source}\" document. You can always enter the value yourself."
    },
    "household": "Me & family",
    "overview": "Overall view",
    "export": {
      "action": "Export as PDF",
      "preview": "Preview",
      "summary": "PDF is being generated",
      "detail": "The download will start automatically once the export is generated."
    },
    "roles": {
      "primary": "Me",
      "partner": "Partner",
      "child": "Child",
      "other": "Further person"
    },
    "tabs": {
      "income": "Income",
      "expenses": "Expenses",
      "assets": "Assets",
      "liabilities": "Liabilities",
      "documents": "Documents"
    }
  }
}
</i18n>
